/**
 * Markdown Navigation Utilities
 *
 * Provides functions for parsing markdown headers and building navigation trees.
 */

export interface MarkdownHeader {
  level: number;
  text: string;
  id: string;
}

export interface NavigationNode extends MarkdownHeader {
  children: NavigationNode[];
}

/**
 * Parse markdown headers from text.
 *
 * This improved version:
 * - Skips headers inside fenced code blocks (```)
 * - Handles duplicate headers by appending a counter
 * - Properly trims and sanitizes header text
 */
export function parseMarkdownHeaders(markdown: string): MarkdownHeader[] {
  const headers: MarkdownHeader[] = [];
  const lines = markdown.split('\n');
  const idCounts: Record<string, number> = {};

  let inCodeBlock = false;

  for (const line of lines) {
    // Track code block state (``` or ~~~)
    if (line.trim().startsWith('```') || line.trim().startsWith('~~~')) {
      inCodeBlock = !inCodeBlock;
      continue;
    }

    // Skip headers inside code blocks
    if (inCodeBlock) {
      continue;
    }

    // Match markdown headers (# to ######)
    const match = line.match(/^(#{1,6})\s+(.+)$/);
    if (match) {
      const level = match[1].length;
      // Clean the header text - remove trailing hashes and whitespace
      const text = match[2].replace(/\s+#+\s*$/, '').trim();

      // Generate base ID
      const baseId = text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      // Handle duplicate IDs by appending a counter
      let id = baseId;
      if (idCounts[baseId] !== undefined) {
        idCounts[baseId]++;
        id = `${baseId}-${idCounts[baseId]}`;
      } else {
        idCounts[baseId] = 0;
      }

      headers.push({ level, text, id });
    }
  }

  return headers;
}

/**
 * Build a hierarchical tree from flat headers.
 * Headers at a lower level become children of the most recent higher-level header.
 */
export function buildNavigationTree(headers: MarkdownHeader[]): NavigationNode[] {
  const root: NavigationNode[] = [];
  const stack: NavigationNode[] = [];

  for (const header of headers) {
    const node: NavigationNode = {
      ...header,
      children: [],
    };

    // Pop items from stack until we find a parent with a smaller level
    while (stack.length > 0 && stack[stack.length - 1].level >= header.level) {
      stack.pop();
    }

    if (stack.length === 0) {
      // Top-level header
      root.push(node);
    } else {
      // Add as child of the last item in stack
      stack[stack.length - 1].children.push(node);
    }

    stack.push(node);
  }

  return root;
}

/**
 * Generate a unique ID for a header within a specific context (e.g., iteration).
 */
export function generateScopedHeaderId(scopeId: string | number, headerId: string): string {
  return `${scopeId}-${headerId}`;
}

/**
 * Get all header IDs from a tree (flattened).
 */
export function flattenHeaderIds(nodes: NavigationNode[]): string[] {
  const ids: string[] = [];

  function traverse(node: NavigationNode) {
    ids.push(node.id);
    node.children.forEach(traverse);
  }

  nodes.forEach(traverse);
  return ids;
}
